import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

// Safe affiliate data (excludes banking info)
export interface AffiliateSafe {
  id: string;
  user_id: string;
  full_name: string;
  email: string;
  cpf_cnpj: string;
  phone: string;
  profile_type: 'influencer' | 'health_professional' | 'content_creator' | 'aesthetician_clinic' | 'blog_site' | 'other';
  profession?: string;
  professional_council?: string;
  council_number?: string;
  council_state?: string;
  main_channel: string;
  main_channel_url: string;
  other_channels?: string;
  affiliate_code: string;
  affiliate_link: string;
  status: 'pending' | 'approved' | 'rejected' | 'suspended';
  ethics_accepted: boolean;
  privacy_accepted: boolean;
  terms_accepted: boolean;
  terms_accepted_at?: string;
  created_at: string;
  updated_at: string;
  approved_at?: string;
  has_payment_info: boolean;
}

// Banking data - only fetched via secure Edge Function
export interface AffiliateBankingData {
  bank_name?: string;
  bank_agency?: string;
  bank_account?: string;
  bank_account_type?: string;
  pix_key?: string;
}

export interface AffiliateSale {
  id: string;
  affiliate_id: string;
  order_id: string;
  order_date: string;
  product_name: string;
  product_sku?: string;
  sale_value: number;
  commission_rate: number;
  commission_value: number;
  status: 'pending' | 'confirmed' | 'paid' | 'cancelled';
  click_id?: string;
  customer_email?: string;
  created_at: string;
  confirmed_at?: string;
  paid_at?: string;
}

export interface AffiliatePayment {
  id: string;
  affiliate_id: string;
  amount: number;
  period_start: string;
  period_end: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  payment_method?: string;
  transaction_id?: string;
  notes?: string;
  created_at: string;
  processed_at?: string;
  completed_at?: string;
}

export interface AffiliateClick {
  id: string;
  affiliate_id: string;
  click_id: string;
  ip_address?: string;
  user_agent?: string;
  referrer?: string;
  landing_page?: string;
  clicked_at: string;
}

export interface AffiliateRegistrationData {
  full_name: string;
  email: string;
  cpf_cnpj: string;
  phone: string;
  profile_type: AffiliateSafe['profile_type'];
  profession?: string;
  professional_council?: string;
  council_number?: string;
  council_state?: string;
  main_channel: string;
  main_channel_url: string;
  other_channels?: string;
  ethics_accepted: boolean;
  privacy_accepted: boolean;
  terms_accepted: boolean;
}

interface AffiliateContextType {
  affiliate: AffiliateSafe | null;
  bankingData: AffiliateBankingData | null;
  isLoading: boolean;
  isBankingLoading: boolean;
  isAffiliate: boolean;
  isApprovedAffiliate: boolean;
  sales: AffiliateSale[];
  payments: AffiliatePayment[];
  clicks: AffiliateClick[];
  stats: {
    totalClicks: number;
    totalSales: number;
    conversionRate: number;
    pendingCommissions: number;
    confirmedCommissions: number;
    paidCommissions: number;
    nextPaymentDate: string | null;
  };
  registerAffiliate: (data: AffiliateRegistrationData) => Promise<{ error: Error | null }>;
  updateBankingData: (data: AffiliateBankingData) => Promise<{ error: Error | null }>;
  fetchAffiliate: () => Promise<void>;
  fetchBankingData: () => Promise<void>;
  fetchSales: () => Promise<void>;
  fetchPayments: () => Promise<void>;
  fetchClicks: () => Promise<void>;
}

const AffiliateContext = createContext<AffiliateContextType | undefined>(undefined);

export const AffiliateProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [affiliate, setAffiliate] = useState<AffiliateSafe | null>(null);
  const [bankingData, setBankingData] = useState<AffiliateBankingData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isBankingLoading, setIsBankingLoading] = useState(false);
  const [sales, setSales] = useState<AffiliateSale[]>([]);
  const [payments, setPayments] = useState<AffiliatePayment[]>([]);
  const [clicks, setClicks] = useState<AffiliateClick[]>([]);

  const fetchAffiliate = async () => {
    if (!user) {
      setAffiliate(null);
      setIsLoading(false);
      return;
    }

    try {
      // affiliates_safe is readable only by signed-in users. Make sure the
      // session is valid (refreshing if needed) before querying, otherwise the
      // request goes out as anonymous and is denied.
      const { data: sessionData } = await supabase.auth.getSession();
      let session = sessionData.session;
      if (!session || (session.expires_at && session.expires_at * 1000 < Date.now() + 30_000)) {
        const { data: refreshed } = await supabase.auth.refreshSession();
        session = refreshed.session;
      }
      if (!session) {
        setAffiliate(null);
        return;
      }

      // Use the safe view that excludes banking data
      const { data, error } = await supabase
        .from('affiliates_safe')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;
      setAffiliate(data as AffiliateSafe | null);
    } catch (error) {
      console.error('Error fetching affiliate:', error);
      setAffiliate(null);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchBankingData = async () => {
    if (!user || !affiliate) {
      setBankingData(null);
      return;
    }

    setIsBankingLoading(true);
    try {
      // Get session for auth header
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No session');
      }

      // Fetch banking data via secure Edge Function
      const response = await supabase.functions.invoke('affiliate-banking', {
        method: 'GET',
      });

      if (response.error) {
        throw response.error;
      }

      setBankingData(response.data?.data || null);
    } catch (error) {
      console.error('Error fetching banking data:', error);
      setBankingData(null);
    } finally {
      setIsBankingLoading(false);
    }
  };

  const fetchSales = async () => {
    if (!affiliate) return;

    try {
      const { data, error } = await supabase
        .from('affiliate_sales_safe')
        .select('*')
        .eq('affiliate_id', affiliate.id)
        .order('order_date', { ascending: false });

      if (error) throw error;
      setSales((data as AffiliateSale[]) || []);
    } catch (error) {
      console.error('Error fetching sales:', error);
    }
  };

  const fetchPayments = async () => {
    if (!affiliate) return;

    try {
      const { data, error } = await supabase
        .from('affiliate_payments')
        .select('*')
        .eq('affiliate_id', affiliate.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPayments((data as AffiliatePayment[]) || []);
    } catch (error) {
      console.error('Error fetching payments:', error);
    }
  };

  const fetchClicks = async () => {
    if (!affiliate) return;

    try {
      const { data, error } = await supabase
        .from('affiliate_clicks')
        .select('*')
        .eq('affiliate_id', affiliate.id)
        .order('clicked_at', { ascending: false });

      if (error) throw error;
      setClicks((data as AffiliateClick[]) || []);
    } catch (error) {
      console.error('Error fetching clicks:', error);
    }
  };

  const registerAffiliate = async (data: AffiliateRegistrationData) => {
    if (!user) {
      return { error: new Error('Usuário não autenticado') };
    }

    try {
      // Use database function for atomic, unique code generation (prevents race conditions)
      const { data: codeData, error: codeError } = await supabase.rpc('generate_affiliate_code');
      
      if (codeError) {
        console.error('Error generating affiliate code:', codeError);
        throw codeError;
      }
      
      const affiliateCode = codeData as string;
      const affiliateLink = `${window.location.origin}?ref=${affiliateCode}`;

      const { error } = await supabase
        .from('affiliates')
        .insert({
          user_id: user.id,
          ...data,
          affiliate_code: affiliateCode,
          affiliate_link: affiliateLink,
          terms_accepted_at: new Date().toISOString(),
        });

      if (error) throw error;

      await fetchAffiliate();
      return { error: null };
    } catch (error) {
      console.error('Error registering affiliate:', error);
      return { error: error as Error };
    }
  };

  const updateBankingData = async (data: AffiliateBankingData) => {
    if (!user || !affiliate) {
      return { error: new Error('Usuário não autenticado ou não é afiliado') };
    }

    try {
      // Update banking data via secure Edge Function
      const response = await supabase.functions.invoke('affiliate-banking', {
        method: 'POST',
        body: data,
      });

      if (response.error) {
        throw response.error;
      }

      if (!response.data?.success) {
        throw new Error('Failed to update banking data');
      }

      // Refresh banking data
      await fetchBankingData();
      return { error: null };
    } catch (error) {
      console.error('Error updating banking data:', error);
      return { error: error as Error };
    }
  };

  useEffect(() => {
    fetchAffiliate();
  }, [user]);

  useEffect(() => {
    if (affiliate && affiliate.status === 'approved') {
      fetchSales();
      fetchPayments();
      fetchClicks();
      fetchBankingData();
    }
  }, [affiliate]);

  const stats = {
    totalClicks: clicks.length,
    totalSales: sales.filter(s => s.status !== 'cancelled').length,
    conversionRate: clicks.length > 0 
      ? (sales.filter(s => s.status !== 'cancelled').length / clicks.length) * 100 
      : 0,
    pendingCommissions: sales
      .filter(s => s.status === 'pending')
      .reduce((sum, s) => sum + Number(s.commission_value), 0),
    confirmedCommissions: sales
      .filter(s => s.status === 'confirmed')
      .reduce((sum, s) => sum + Number(s.commission_value), 0),
    paidCommissions: sales
      .filter(s => s.status === 'paid')
      .reduce((sum, s) => sum + Number(s.commission_value), 0),
    nextPaymentDate: payments.find(p => p.status === 'pending')?.period_end || null,
  };

  return (
    <AffiliateContext.Provider
      value={{
        affiliate,
        bankingData,
        isLoading,
        isBankingLoading,
        isAffiliate: !!affiliate,
        isApprovedAffiliate: affiliate?.status === 'approved',
        sales,
        payments,
        clicks,
        stats,
        registerAffiliate,
        updateBankingData,
        fetchAffiliate,
        fetchBankingData,
        fetchSales,
        fetchPayments,
        fetchClicks,
      }}
    >
      {children}
    </AffiliateContext.Provider>
  );
};

export const useAffiliate = () => {
  const context = useContext(AffiliateContext);
  if (context === undefined) {
    throw new Error('useAffiliate must be used within an AffiliateProvider');
  }
  return context;
};
